import { useState } from "react";
import {
  Modal,
  BlockStack,
  InlineStack,
  Box,
  Text,
  ResourceList,
  ResourceItem,
  Checkbox,
  EmptyState,
  Bleed,
  Badge,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { Event, FAQItem } from "@features/events/types";
import EventBadgeRenderer from "@features/events/components/EventBadgeRenderer";
import { RenderDate } from "@shared/ui";
import { formatStartDate } from "@shared/lib";
import { getLocalizedText } from "@shared/lib";

interface ImportFAQModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (faqItems: FAQItem[]) => void;
  eventsWithFAQ: Event[];
  loading: boolean;
}

export default function ImportFAQModal({
  isOpen,
  onClose,
  onImport,
  eventsWithFAQ,
  loading,
}: ImportFAQModalProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [selectedFAQIndices, setSelectedFAQIndices] = useState<number[]>([]);

  const resetState = () => {
    setCurrentStep(1);
    setSelectedEvent(null);
    setSelectedFAQIndices([]);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleEventSelect = (event: Event) => {
    setSelectedEvent(event);
    setSelectedFAQIndices([]);
    setCurrentStep(2);
  };

  const handleBack = () => {
    setCurrentStep(1);
    setSelectedEvent(null);
    setSelectedFAQIndices([]);
  };

  const handleImport = () => {
    if (selectedEvent && selectedFAQIndices.length > 0) {
      const faqItemsToImport = selectedFAQIndices.map(
        (index) => selectedEvent.faq![index]
      );
      onImport(faqItemsToImport);
      handleClose();
    }
  };

  const toggleFAQSelection = (index: number) => {
    if (selectedFAQIndices.includes(index)) {
      setSelectedFAQIndices(selectedFAQIndices.filter((i) => i !== index));
    } else {
      setSelectedFAQIndices([...selectedFAQIndices, index]);
    }
  };

  // Step 1: Select Event
  const renderStep1 = () => (
    <Modal.Section>
      <Bleed marginInline="400" marginBlock="400">
        {loading ? (
          <Box padding="400">
            <BlockStack gap="200">
              <Text as="p" variant="bodySm" tone="subdued">
                {t("common.loading")}
              </Text>
            </BlockStack>
          </Box>
        ) : eventsWithFAQ.length === 0 ? (
          <Box padding="400">
            <EmptyState
              heading={t("eventDetails.faq.import.noEventsWithFAQ")}
              image=""
            >
              <Text as="p" variant="bodySm" tone="subdued">
                {t("eventDetails.faq.import.noEventsWithFAQDescription")}
              </Text>
            </EmptyState>
          </Box>
        ) : (
          <ResourceList
            resourceName={{ singular: "event", plural: "events" }}
            items={eventsWithFAQ}
            renderItem={(event: Event) => {
              const title = getLocalizedText(event.title);
              const faqCount = event.faq?.length || 0;

              return (
                <ResourceItem
                  id={event.id}
                  accessibilityLabel={`Select ${title}`}
                  onClick={() => handleEventSelect(event)}
                  verticalAlignment="center"
                >
                  <InlineStack
                    align="space-between"
                    blockAlign="center"
                    wrap={false}
                  >
                    <BlockStack gap="100">
                      <InlineStack gap="200" blockAlign="center">
                        <Text
                          as="h3"
                          variant="bodyMd"
                          fontWeight="bold"
                          breakWord
                        >
                          {title}
                        </Text>
                      </InlineStack>
                        <EventBadgeRenderer
                        slug={event.status ?? ""}
                        entity="event"
                        />
                      <Text as="span" variant="bodySm" tone="subdued">
                        {event.dateStart === event.dateEnd && event.dateStart ? (
                          <RenderDate dayFirst={true} date={event.dateStart} />
                        ) : (
                          <>
                            {formatStartDate(event.dateStart)}
                            {event.dateEnd && (
                              <>
                                {" "}
                                -{" "}
                                <RenderDate
                                  dayFirst={true}
                                  date={event.dateEnd}
                                />
                              </>
                            )}
                          </>
                        )}
                      </Text>
                    </BlockStack>
                    <Badge>{faqCount.toString()}</Badge>
                  </InlineStack>
                </ResourceItem>
              );
            }}
          />
        )}
      </Bleed>
    </Modal.Section>
  );

  // Step 2: Select FAQ Items
  const renderStep2 = () => {
    const faqItems = selectedEvent?.faq || [];

    if (faqItems.length === 0) {
      return (
        <Modal.Section>
          <Box padding="400">
            <EmptyState
              heading={t("eventDetails.faq.import.noFAQItems")}
              action={{
                content: t("common.back"),
                onAction: handleBack,
              }}
              image=""
            >
              <Text as="p" variant="bodySm" tone="subdued">
                {t("eventDetails.faq.import.noFAQItemsDescription")}
              </Text>
            </EmptyState>
          </Box>
        </Modal.Section>
      );
    }

    return (
      <Modal.Section>
        <BlockStack gap="300">
          {faqItems.map((faqItem, index) => {
            const question = getLocalizedText(faqItem.question);
            const answer = getLocalizedText(faqItem.answer);
            const isSelected = selectedFAQIndices.includes(index);

            return (
              <Box
                key={index}
                padding="300"
                borderColor="border"
                borderWidth="025"
                borderRadius="200"
                background={isSelected ? "bg-surface-selected" : "bg-surface"}
              >
                <InlineStack gap="300" blockAlign="start">
                  <Checkbox
                    label=""
                    checked={isSelected}
                    onChange={() => toggleFAQSelection(index)}
                  />
                  <div
                    style={{ flex: 1, cursor: "pointer" }}
                    onClick={() => toggleFAQSelection(index)}
                  >
                    <BlockStack gap="100">
                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                        {question}
                      </Text>
                      <Text
                        as="p"
                        variant="bodySm"
                        tone="subdued"
                        truncate
                      >
                        {answer}
                      </Text>
                    </BlockStack>
                  </div>
                </InlineStack>
              </Box>
            );
          })}
        </BlockStack>
      </Modal.Section>
    );
  };

  // Modal configuration based on step
  const getModalConfig = () => {
    if (currentStep === 1) {
      return {
        title: t("eventDetails.faq.import.selectEvent"),
        primaryAction: undefined,
        secondaryActions: undefined,
      };
    }

    // Step 2
    const hasFAQItems = (selectedEvent?.faq?.length || 0) > 0;
    
    return {
      title: t("eventDetails.faq.import.selectFAQItems"),
      primaryAction: hasFAQItems
        ? {
            content: t("eventDetails.faq.import.import"),
            onAction: handleImport,
            disabled: selectedFAQIndices.length === 0,
          }
        : undefined,
      secondaryActions: [
        {
          content: t("common.back"),
          onAction: handleBack,
        },
      ],
    };
  };

  const modalConfig = getModalConfig();

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={modalConfig.title}
      primaryAction={modalConfig.primaryAction}
      secondaryActions={modalConfig.secondaryActions}
      onTransitionEnd={() => {
        document.body.setAttribute("data-lock-scrolling-hidden", "true");
      }}
    >
      {currentStep === 1 ? renderStep1() : renderStep2()}
    </Modal>
  );
}
